/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useQuery, useMutation } from '@apollo/client';
import { USER_STATE_AUTHENTICATED } from '../../modules/utils/constants/constants';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import {
  DATALAYER_PAGE_EVENT,
  PROMOTIONS_CONSTANT,
  PageContext,
  addSubDirectoryPrefix,
  generateApolloClientHeaders,
  updateDataLayerProperties,
  updateImageDomain,
} from '../../modules';
import {
  phoenixAccountGetGlobalPromotionDetail,
  phoenixAccountGetGlobalMarketingPromotionDetail,
  phoenixAccountRegisterPromotion,
  phoenixAccountGetLoyaltyPromotionDetail,
  phoenixAccountGetLoyaltyMarketingPromotionDetail,
} from '../../modules/graph';
import { useStore } from '../../modules/store/memberLevelStore';
import { SkeletonLoaderV2 } from '../../molecules/SkeletonLoaderV2';
import { useSession } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

import PromotionBannerMockdata from './__mock__/PromotionBanner.model.json';
import { StyledPromotionBanner } from './PromotionBanner.styles';
import { mapPromotionByIdToLegacy } from './mapper';
import { mapCustomerPromotionByIdToLegacy } from './loyaltypromotion.mapper';

const BannerContent = dynamic(() => import('./BannerContent/BannerContent').then(mod => mod.BannerContent));

export const PromotionBanner = (props: any) => {
  const { model } = props;
  const { IS_LOCAL_DEV } = process.env;
  const isAuthorMode = props?.isAuthorMode;
  const { isACDLenabled } = useSession();
  const router = useRouter();
  const promotion = router && router.query['promotion'];
  const { promotionDetails, setPromotionDetails, setIsPromotionDetailsLoading } = useStore(state => state);
  const { promoRegistered, setPromoRegistered } = useAccountPersistentStore(state => state);
  const pageContext = useContext(PageContext);
  const dataLoaded = useRef<boolean>(false);
  const skipDatalayerUpdate = useRef<boolean>(false);
  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const isLoggedIn = pageContext?.userState === USER_STATE_AUTHENTICATED;
  const currentLocale = (pageContext?.currentLocale || 'en-US') as string; // normalize to en-US if needed

  const featureFlagPromotion =
    pageContext?.featureFlags?.[FEATURE_FLAGS?.EXPERIENCE_MCOM_ACCOUNT_ENABLE_MBOP_PROMOTIONS];

  // Memoize all variables that affect the query,
  // to prevent re-triggering useQuery if component re-renders.
  const skipQuery =
    useMemo(() => {
      return !pageContext?.sessionData && !isAuthorMode;
    }, [pageContext, isAuthorMode]) ||
    dataLoaded.current ||
    isAuthorMode;

  const promotionQuery = useMemo(() => {
    if (isLoggedIn) {
      return featureFlagPromotion
        ? phoenixAccountGetLoyaltyMarketingPromotionDetail
        : phoenixAccountGetLoyaltyPromotionDetail;
    }
    return featureFlagPromotion
      ? phoenixAccountGetGlobalMarketingPromotionDetail
      : phoenixAccountGetGlobalPromotionDetail;
  }, [isLoggedIn, featureFlagPromotion]);

  const variables = useMemo(() => {
    if (isLoggedIn) {
      return featureFlagPromotion
        ? { promotionId: promotion || undefined }
        : {
            customerId: sessionData?.consumerID,
            loyaltyPromotionId: promotion || undefined,
          };
    }
    return {
      promotionId: promotion || undefined,
    };
  }, [isLoggedIn, sessionData?.consumerID, promotion]);

  const { loading: loadingLoyaltyPromotion, error: errorLoyaltyPromotion } = useQuery(promotionQuery, {
    variables,
    skip: skipQuery,
    context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
    onCompleted: (data: any) => {
      const legacy = featureFlagPromotion
        ? isLoggedIn
          ? mapCustomerPromotionByIdToLegacy({ data }, currentLocale)?.data
          : mapPromotionByIdToLegacy({ data }, currentLocale)?.data
        : data;
      const withDomain = updateImageDomain(legacy, currentLocale);
      setPromotionDetails(withDomain);
      dataLoaded.current = true;
    },
  });

  useEffect(() => {
    if (isAuthorMode) {
      setPromotionDetails(PromotionBannerMockdata.data);
    }
  }, []);

  useEffect(() => {
    if (promoRegistered && !skipDatalayerUpdate?.current) {
      if (!isACDLenabled) {
        const dataLayerProps = { mr_register_for_promo: promoRegistered };
        updateDataLayerProperties(dataLayerProps, DATALAYER_PAGE_EVENT.PROMOTION);
      }
      setPromoRegistered && setPromoRegistered('');
    }
  }, [promoRegistered, isACDLenabled]);

  useEffect(() => {
    setIsPromotionDetailsLoading(loadingLoyaltyPromotion);
  }, [loadingLoyaltyPromotion]);

  const [registerLoyaltyPromotions] = useMutation(phoenixAccountRegisterPromotion);

  useEffect(() => {
    if (errorLoyaltyPromotion) {
      routeToUnavailable();
    }
  }, [errorLoyaltyPromotion]);

  const routeToUnavailable = () => {
    if (promotion) {
      router.push({
        pathname: addSubDirectoryPrefix(PROMOTIONS_CONSTANT.PROMOTION_UNAVAILABLE_URL),
        query: { promotion: promotion },
      });
    }
  };

  const registerPromotion = () => {
    registerLoyaltyPromotions({
      variables: {
        input: {
          customerId: sessionData?.consumerID,
          ids: [{ id: promotion && promotion }],
        },
      },
      context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
      onCompleted: () => {
        skipDatalayerUpdate.current = true;
        setPromoRegistered && setPromoRegistered(promotion?.toString());
        window.location.reload();
      },
    });
  };

  const showSkeleton = loadingLoyaltyPromotion || !promotionDetails;

  return (
    <StyledPromotionBanner
      data-component-name="o-account-promotionbanner"
      data-testid="promotionbanner"
      className="fullbleed m-container-fullbleed"
    >
      <div className="pb-item">
        {showSkeleton ? (
          <div className=" pb-item__skeleton d-flex d-md-block d-lg-flex flex-column align-items-center align-items-lg-start offset-md-0 offset-lg-0">
            <SkeletonLoaderV2 className="mx-sm-auto mx-md-0 pb-item__skeleton__img" />
          </div>
        ) : (
          <BannerContent
            model={model}
            promotionDetail={promotionDetails}
            locale={currentLocale}
            isLoggedIn={isLoggedIn}
            registerPromotion={registerPromotion}
          />
        )}
      </div>
    </StyledPromotionBanner>
  );
};
